// SPY (ჯაშუში) — everyone knows the secret word except the spies.
// 3–5 generic questions are answered (out loud or in writing), then the group votes,
// the app reveals who the spies were, and caught spies get one last guess.

import type { SpyPack } from "../content";
import {
  activePlayers, bool, clampInt, oneOf, type Engine, type EngineCtx, type TimedState,
} from "../engine";
import { cleanText } from "../text";
import type { ErrorCode, GameResultSummary, Lang, RoomCore } from "../types";

export interface SpySettings {
  packs: string[];
  adult: boolean;
  spies: number; // 0 = auto by player count
  questions: number; // 3..5
  mode: "voice" | "text";
  spyHint: boolean; // spy sees the pack name
  qTime: number; // seconds per question, 0 = host advances
}

type Phase = "roles" | "question" | "answers" | "vote" | "reveal" | "guess" | "result";

export interface SpyState extends TimedState {
  phase: Phase;
  packId: string;
  packName: string;
  packEmoji: string;
  word: string;
  options: string[]; // guess options for caught spies (contains word)
  spies: string[];
  players: string[];
  order: string[];
  seen: string[];
  questions: string[];
  qi: number;
  speaker: number;
  answers: Record<string, string>[]; // per question
  votes: Record<string, string>;
  accused: string[];
  caught: string[];
  guesses: Record<string, string>;
  winner: "civ" | "spy" | null;
  scores: Record<string, number>;
  roundPts: Record<string, number>;
  roundNo: number;
}

const VOTE_MS = 75_000;
const ANSWERS_MS = 14_000;
const TEXT_MS = 60_000;
const REVEAL_MS = 7_500;
const GUESS_MS = 35_000;
export const SPY_MAX_ANSWER = 40;

export function autoSpies(n: number): number {
  return n >= 10 ? 3 : n >= 6 ? 2 : 1;
}

function setPhase(s: SpyState, phase: Phase, now: number, ms: number | null) {
  s.phase = phase;
  s.deadline = ms === null ? null : now + ms;
  s.phaseMs = ms ?? 0;
}

function choosePack(room: RoomCore<SpySettings>, ctx: EngineCtx): SpyPack | null {
  const data = ctx.content.spy(room.lang);
  let packs = data.packs.filter((p) => room.settings.adult || !p.adult);
  if (room.settings.packs.length) {
    const chosen = packs.filter((p) => room.settings.packs.includes(p.id));
    if (chosen.length) packs = chosen;
  }
  packs = packs.filter((p) => p.words.length >= 8);
  return packs.length ? ctx.rng.pick(packs) : null;
}

function questionStart(s: SpyState, room: RoomCore<SpySettings>, now: number) {
  s.speaker = 0;
  if (room.settings.mode === "text") setPhase(s, "question", now, TEXT_MS);
  else setPhase(s, "question", now, room.settings.qTime > 0 ? room.settings.qTime * 1000 : null);
}

function afterQuestion(s: SpyState, room: RoomCore<SpySettings>, ctx: EngineCtx) {
  if (room.settings.mode === "text" && s.phase === "question") {
    setPhase(s, "answers", ctx.now, ANSWERS_MS);
    return;
  }
  if (s.qi + 1 < s.questions.length) {
    s.qi += 1;
    questionStart(s, room, ctx.now);
  } else {
    setPhase(s, "vote", ctx.now, room.local ? null : VOTE_MS);
  }
}

function resolveVotes(s: SpyState, room: RoomCore<SpySettings>, ctx: EngineCtx, forced?: string[]) {
  if (forced) {
    s.accused = forced.filter((id) => s.players.includes(id));
  } else {
    const counts = new Map<string, number>();
    for (const t of Object.values(s.votes)) counts.set(t, (counts.get(t) ?? 0) + 1);
    const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1]);
    const k = s.spies.length;
    const boundary = sorted[k]?.[1] ?? 0; // votes of the first player outside the top-k
    s.accused = sorted.slice(0, k).filter(([, c]) => c > boundary && c > 0).map(([id]) => id);
  }
  s.caught = s.accused.filter((id) => s.spies.includes(id));
  setPhase(s, "reveal", ctx.now, room.local ? null : REVEAL_MS);
}

function finish(s: SpyState, ctx: EngineCtx) {
  const allCaught = s.spies.every((id) => s.caught.includes(id));
  const guessedRight = s.caught.some((id) => s.guesses[id] === s.word);
  s.winner = allCaught && !guessedRight ? "civ" : "spy";
  s.roundPts = {};
  const add = (id: string, n: number) => {
    s.roundPts[id] = (s.roundPts[id] ?? 0) + n;
    s.scores[id] = (s.scores[id] ?? 0) + n;
  };
  for (const id of s.players) {
    if (s.spies.includes(id)) {
      if (s.winner === "spy") add(id, s.caught.includes(id) ? 2 : 4);
      if (s.caught.includes(id) && s.guesses[id] === s.word) add(id, 1);
    } else {
      if (s.winner === "civ") add(id, 2);
      if (s.votes[id] && s.spies.includes(s.votes[id])) add(id, 1);
    }
  }
  setPhase(s, "result", ctx.now, null);
}

export const spyEngine: Engine<SpyState, SpySettings> = {
  id: "spy",
  minPlayers: 3,
  maxPlayers: 16,
  supportsBots: false,

  defaults(_lang: Lang): SpySettings {
    return { packs: [], adult: false, spies: 0, questions: 4, mode: "voice", spyHint: true, qTime: 0 };
  },

  sanitize(input: any, prev: SpySettings): SpySettings {
    const i = input && typeof input === "object" ? input : {};
    const packs = Array.isArray(i.packs)
      ? Array.from(new Set(i.packs.filter((x: unknown) => typeof x === "string" && /^[a-z0-9_-]{1,24}$/.test(x)))) as string[]
      : prev.packs;
    return {
      packs,
      adult: bool(i.adult, prev.adult),
      spies: clampInt(i.spies, 0, 4, prev.spies),
      questions: clampInt(i.questions, 3, 5, prev.questions),
      mode: oneOf(i.mode, ["voice", "text"] as const, prev.mode),
      spyHint: bool(i.spyHint, prev.spyHint),
      qTime: oneOf(clampInt(i.qTime, 0, 120, prev.qTime), [0, 45, 60, 90] as const, prev.qTime as 0),
    };
  },

  start(room, ctx) {
    const pack = choosePack(room, ctx);
    if (!pack) return "no_content" as ErrorCode;
    const data = ctx.content.spy(room.lang);
    const players = activePlayers(room).map((p) => p.id);
    const used = new Set(room.usedContent);
    const fresh = pack.words.filter((w) => !used.has(`spy:${pack.id}:${w}`));
    const word = ctx.rng.pick(fresh.length ? fresh : pack.words);
    room.usedContent.push(`spy:${pack.id}:${word}`);
    const n = room.settings.spies > 0 ? room.settings.spies : autoSpies(players.length);
    const spyCount = Math.max(1, Math.min(n, Math.floor((players.length - 1) / 2) || 1));
    const spies = ctx.rng.shuffle(players).slice(0, spyCount);
    const want = room.settings.questions;
    const packQs = ctx.rng.shuffle(pack.questions).slice(0, Math.min(2, pack.questions.length));
    const generalQs = ctx.rng.shuffle(data.general).filter((q) => !packQs.includes(q));
    const questions = ctx.rng.shuffle([...packQs, ...generalQs.slice(0, Math.max(0, want - packQs.length))]).slice(0, want);
    const decoys = ctx.rng.shuffle(pack.words.filter((w) => w !== word)).slice(0, 7);
    const prev = room.state as SpyState | null;
    const s: SpyState = {
      phase: "roles", deadline: null, phaseMs: 0,
      packId: pack.id, packName: pack.name, packEmoji: pack.emoji, word,
      options: ctx.rng.shuffle([word, ...decoys]),
      spies, players, order: ctx.rng.shuffle(players), seen: [],
      questions, qi: 0, speaker: 0, answers: questions.map(() => ({})),
      votes: {}, accused: [], caught: [], guesses: {}, winner: null,
      scores: prev && prev.scores ? { ...prev.scores } : {},
      roundPts: {}, roundNo: (prev?.roundNo ?? 0) + 1,
    };
    for (const id of players) s.scores[id] ??= 0;
    return s;
  },

  act(room, s, actorId, a, ctx) {
    if (!a || typeof a !== "object") return "bad_input";
    const isHost = room.hostId === actorId || room.local;
    const inGame = s.players.includes(actorId);
    // In local (pass-and-play) mode the single device acts on behalf of `a.as`.
    const who: string = room.local && typeof a.as === "string" ? a.as : actorId;
    switch (a.t) {
      case "seen": {
        if (s.phase !== "roles") return "bad_phase";
        if (!s.players.includes(who)) return "not_player";
        if (!s.seen.includes(who)) s.seen.push(who);
        if (s.players.every((id) => s.seen.includes(id))) questionStart(s, room, ctx.now);
        return s;
      }
      case "startQuestions": {
        if (!isHost) return "not_host";
        if (s.phase !== "roles") return "bad_phase";
        questionStart(s, room, ctx.now);
        return s;
      }
      case "next": {
        // Voice mode: the current speaker (or host) passes the turn.
        if (s.phase === "question" && room.settings.mode === "voice") {
          const current = s.order[(s.speaker + s.qi) % s.order.length];
          if (!isHost && actorId !== current) return "not_your_turn";
          s.speaker += 1;
          if (s.speaker >= s.order.length) afterQuestion(s, room, ctx);
          return s;
        }
        if (!isHost) return "not_host";
        if (s.phase === "question") { afterQuestion(s, room, ctx); return s; }
        if (s.phase === "answers") { afterQuestion(s, room, ctx); return s; }
        if (s.phase === "reveal") {
          if (s.caught.length) setPhase(s, "guess", ctx.now, room.local ? null : GUESS_MS);
          else finish(s, ctx);
          return s;
        }
        return "bad_phase";
      }
      case "skipToVote": {
        if (!isHost) return "not_host";
        if (s.phase !== "question" && s.phase !== "answers") return "bad_phase";
        setPhase(s, "vote", ctx.now, room.local ? null : VOTE_MS);
        return s;
      }
      case "answer": {
        if (s.phase !== "question" || room.settings.mode !== "text") return "bad_phase";
        if (!s.players.includes(who)) return "not_player";
        const text = cleanText(a.text, SPY_MAX_ANSWER);
        if (!text) return "bad_input";
        s.answers[s.qi][who] = text;
        if (s.players.every((id) => s.answers[s.qi][id])) afterQuestion(s, room, ctx);
        return s;
      }
      case "vote": {
        if (s.phase !== "vote") return "bad_phase";
        if (!s.players.includes(who)) return "not_player";
        if (!s.players.includes(a.target)) return "bad_input";
        if (a.target === who) return "self_vote";
        s.votes[who] = a.target;
        if (s.players.every((id) => s.votes[id])) resolveVotes(s, room, ctx);
        return s;
      }
      case "accuse": {
        // Group decision (pass-and-play) or host override when the room can't agree.
        if (!isHost) return "not_host";
        if (s.phase !== "vote") return "bad_phase";
        const targets: string[] = Array.isArray(a.targets) ? a.targets.filter((x: unknown) => typeof x === "string") : [];
        resolveVotes(s, room, ctx, Array.from(new Set(targets)).slice(0, s.spies.length));
        return s;
      }
      case "closeVote": {
        if (!isHost) return "not_host";
        if (s.phase !== "vote") return "bad_phase";
        resolveVotes(s, room, ctx);
        return s;
      }
      case "guess": {
        if (s.phase !== "guess") return "bad_phase";
        if (!s.caught.includes(who)) return "not_player";
        if (s.guesses[who]) return "already";
        if (!s.options.includes(a.word)) return "bad_input";
        s.guesses[who] = a.word;
        if (s.caught.every((id) => s.guesses[id])) finish(s, ctx);
        return s;
      }
      default:
        if (!inGame) return "not_player";
        return "bad_input";
    }
  },

  tick(room, s, ctx) {
    switch (s.phase) {
      case "question":
        if (room.settings.mode === "text") afterQuestion(s, room, ctx);
        else {
          // timer ran out for this question: move on
          if (s.qi + 1 < s.questions.length) { s.qi += 1; questionStart(s, room, ctx.now); }
          else setPhase(s, "vote", ctx.now, room.local ? null : VOTE_MS);
        }
        break;
      case "answers":
        afterQuestion(s, room, ctx);
        break;
      case "vote":
        resolveVotes(s, room, ctx);
        break;
      case "reveal":
        if (s.caught.length) setPhase(s, "guess", ctx.now, room.local ? null : GUESS_MS);
        else finish(s, ctx);
        break;
      case "guess":
        finish(s, ctx);
        break;
      default:
        s.deadline = null;
    }
    return s;
  },

  onLeave(room, s, playerId, ctx) {
    s.players = s.players.filter((id) => id !== playerId);
    s.order = s.order.filter((id) => id !== playerId);
    s.spies = s.spies.filter((id) => id !== playerId);
    delete s.votes[playerId];
    if (s.spies.length === 0 && s.phase !== "result") {
      // The only spy left: civilians win by default.
      s.caught = [];
      s.winner = "civ";
      setPhase(s, "result", ctx.now, null);
      return s;
    }
    if (s.phase === "roles" && s.players.every((id) => s.seen.includes(id))) questionStart(s, room, ctx.now);
    if (s.phase === "vote" && s.players.every((id) => s.votes[id])) resolveVotes(s, room, ctx);
    return s;
  },

  view(room, s, viewerId) {
    const isSpy = !!viewerId && s.spies.includes(viewerId);
    const isPlayer = !!viewerId && s.players.includes(viewerId);
    const open = s.phase === "reveal" || s.phase === "guess" || s.phase === "result";
    const showAnswers = room.settings.mode === "text" && (s.phase === "answers" || s.phase === "vote" || open);
    const speakerId = s.phase === "question" && room.settings.mode === "voice" ? s.order[(s.speaker + s.qi) % s.order.length] : null;
    return {
      phase: s.phase,
      deadline: s.deadline,
      phaseMs: s.phaseMs,
      roundNo: s.roundNo,
      players: s.players,
      order: s.questions.length ? [...s.order.slice(s.qi % s.order.length), ...s.order.slice(0, s.qi % s.order.length)] : s.order,
      seen: s.seen,
      role: !isPlayer ? null : isSpy ? "spy" : "civ",
      word: isPlayer && !isSpy ? s.word : open ? s.word : null,
      packName: !isPlayer || !isSpy || room.settings.spyHint || open ? s.packName : null,
      packEmoji: s.packEmoji,
      spyCount: s.spies.length,
      question: s.phase === "question" || s.phase === "answers" ? s.questions[s.qi] : null,
      qi: s.qi,
      qTotal: s.questions.length,
      speaker: speakerId,
      speakerIndex: s.speaker,
      answered: s.phase === "question" ? Object.keys(s.answers[s.qi] ?? {}) : [],
      myAnswer: viewerId ? s.answers[s.qi]?.[viewerId] ?? null : null,
      answers: showAnswers ? s.answers.map((a, i) => ({ q: s.questions[i], a })) .slice(0, s.phase === "answers" ? s.qi + 1 : s.questions.length) : [],
      voted: Object.keys(s.votes),
      myVote: viewerId ? s.votes[viewerId] ?? null : null,
      votes: open ? s.votes : null,
      accused: open ? s.accused : [],
      spies: open ? s.spies : [],
      caught: open ? s.caught : [],
      guessOptions: s.phase === "guess" && viewerId && s.caught.includes(viewerId) ? s.options : room.local && s.phase === "guess" ? s.options : null,
      guesses: s.phase === "result" ? s.guesses : null,
      winner: s.winner,
      scores: s.scores,
      roundPts: s.phase === "result" ? s.roundPts : {},
    };
  },

  isOver(s) {
    return s.phase === "result";
  },

  summary(room, s): GameResultSummary {
    const players = room.players
      .filter((p) => s.players.includes(p.id))
      .map((p) => ({ id: p.id, name: p.name, score: s.scores[p.id] ?? 0, userId: p.userId ?? null, isBot: p.isBot }));
    const winners = s.winner === "spy" ? s.spies : s.players.filter((id) => !s.spies.includes(id));
    return { game: "spy", lang: room.lang, players, winnerIds: winners };
  },
};
